// The design's small charts: the KPI sparkline, the gateway dot meter and
// the 14-day bar chart with its hover tooltip.
import { useEffect, useId, useRef, useState } from 'react';
import { fmtN, niceScale } from '../lib/format';
import { useTip } from './Tip';

export function Spark({ vals, color = 'var(--blue)', w = 92, h = 36 }: { vals: number[]; color?: string; w?: number; h?: number }) {
  const id = 'sp' + useId().replace(/:/g, '');
  if (vals.length < 2) return <svg className="spark" viewBox={`0 0 ${w} ${h}`} aria-hidden="true" />;
  const mx = Math.max(...vals), mn = Math.min(...vals), sx = w / (vals.length - 1);
  const sy = (v: number) => h - 3 - ((v - mn) / (mx - mn || 1)) * (h - 8);
  const d = vals.map((v, i) => (i ? 'L' : 'M') + (i * sx).toFixed(1) + ' ' + sy(v).toFixed(1)).join(' ');
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={{ stopColor: color, stopOpacity: 0.18 }} /><stop offset="1" style={{ stopColor: color, stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <path d={`${d} L${w} ${h} L0 ${h}Z`} fill={`url(#${id})`} />
      <path d={d} fill="none" style={{ stroke: color }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={sy(vals[vals.length - 1])} r="3" style={{ fill: color }} />
    </svg>
  );
}

/** One square per connected home's gateway: blue when heard from, red when silent. */
export function DotMeter({ on, tot, label }: { on: number; tot: number; label: string }) {
  const c = 13, rows = Math.max(1, Math.ceil(tot / c));
  return (
    <svg className="spark" viewBox="0 0 92 36" aria-label={label} role="img">
      {Array.from({ length: Math.min(tot, 65) }, (_, i) => {
        const x = i % c, y = Math.floor(i / c);
        return <rect key={i} x={x * 7.1} y={36 - rows * 7.1 + y * 7.1 + 1} width="5" height="5" rx="1.6" style={{ fill: i < on ? 'var(--blue)' : 'var(--red)' }} opacity={i < on ? 0.85 : 1} />;
      })}
    </svg>
  );
}

interface BarOpts { cur?: boolean; h?: number; aria: string; tip: (i: number) => React.ReactNode; every?: number }

export function BarChart({ vals, labels, o }: { vals: number[]; labels: string[]; o: BarOpts }) {
  const ref = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(600);
  const [hi, setHi] = useState<number | null>(null);
  const tip = useTip();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(260, el.clientWidth || 600)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = o.h ?? 220, pl = 36, pr = 8, pt = 12, pb = 26, iw = W - pl - pr, ih = H - pt - pb;
  const sc = niceScale(Math.max(...vals, 0), 4), mx = sc.mx;
  const step = iw / Math.max(1, vals.length), bw = Math.min(28, step * 0.56);
  return (
    <div className="chart" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={o.aria}>
        {sc.ticks.map((t) => {
          const y = pt + ih - (t / mx) * ih;
          return (
            <g key={t}>
              <line className="gridl" x1={pl} x2={W - pr} y1={y} y2={y} strokeDasharray={t ? '2 4' : undefined} />
              <text className="ax" x={pl - 8} y={y + 4} textAnchor="end">{fmtN(t)}</text>
            </g>
          );
        })}
        {vals.map((v, i) => {
          const x = pl + i * step + (step - bw) / 2, h = Math.max(2, (v / mx) * ih), y = pt + ih - h;
          const cur = i === vals.length - 1 && o.cur;
          return (
            <g key={i}>
              <path className={'bar-b' + (cur ? ' cur' : '') + (hi === i ? ' h' : '')} d={`M${x} ${pt + ih}V${y + 4}a4 4 0 0 1 4-4h${bw - 8}a4 4 0 0 1 4 4V${pt + ih}z`} />
              {(i % (o.every ?? 1) === 0 || i === vals.length - 1) && <text className="ax" x={x + bw / 2} y={H - 8} textAnchor="middle">{labels[i]}</text>}
            </g>
          );
        })}
        {vals.map((_, i) => (
          <rect key={'h' + i} x={pl + i * step} y={pt} width={step} height={ih} fill="transparent"
            onMouseMove={(e) => { setHi(i); tip.show(o.tip(i), e.clientX, e.clientY); }}
            onMouseLeave={() => { setHi(null); tip.hide(); }} />
        ))}
      </svg>
    </div>
  );
}
